import { useScaleStore } from '@/stores/scale'

export function useScale() {
  const $s = useScaleStore()

  const isMounted = useMounted()

  const fontSize = computed(() => $s.scale.fontSize)
  const gridRatio = computed(() => $s.scale.gridRatio)
  const ratio = computed(() => $s.scale.ratio)
  const scales = computed(() => $s.scales)

  let defaultScale: typeof $s.scale

  // Initialize defaultScale on first run.

  const { ww } = useViewport()

  let lastAppliedBreakpoint: number | undefined


  function setScaleVariables() {
    const levels = $s.classScaleLevels
    const html = document.documentElement
    const baseGridUnit = gridRatio.value // Default grid unit in rem units

    for (let i = -5; i <= levels; i += 0.5) {
      const fontSize = ratio.value ** i
      const indexString = generateIndexString(i)


      const property = `--fs-${indexString}`
      const value = `${fontSize}rem`
      // console.log('fs property', property)
      // console.log('fs value', value)
      // Set font size CSS variable
      html.style.setProperty(property, value)

      // Calculate and set grid unit CSS variable
      const gridUnit = calculateGridUnit(fontSize, baseGridUnit)

      const gridProperty = `--gr-${indexString}`
      const gridValue = `${gridUnit}rem`
      // console.log('gr property', gridProperty)
      // console.log('gr value', gridValue)
      html.style.setProperty(gridProperty, gridValue)
    }
  }

  function initGlobalVariables() {
    // calculate the percentage relative to the default browser font-size (usually 16px).
    // If fontSize is 16, then fontSizePercentage will be 100%. If 17, then 106.25% and so on.

    const html = document.documentElement

    const fontSizePercentage = (fontSize.value / 16) * 100
    const bfsRemEquivalent = fontSize.value / 16

    html.style.fontSize = `${fontSizePercentage}%`
    html.style.setProperty('--fs', `${bfsRemEquivalent}rem`) // Set --bfs to its equivalent rem value

    html.style.setProperty('--lh', gridRatio.value.toString())
    html.style.setProperty('--s', $s.scale.ratio.toString())
  }

  function initTypeScales(baseScale?: TypeScaleConfig , scales?: TypeScales) {
    if (typeof document !== 'undefined') {
      // Save the default scale settings on first run
      
      if(baseScale) {
        $s.initBaseScale(baseScale)
      }
      
      if (!scales) {
        defaultScale = { ...$s.scale }
      } else {
        $s.initScales(scales)
      }

      if ($s.active) {
        initGlobalVariables()
        setScaleVariables()
      }
    }
  }

  // New function to calculate grid unit
  function calculateGridUnit(fontSize: number, baseGridUnit: number) {
  // Find the smallest multiple of baseGridUnit that is >= fontSize
    const numberOfUnits = Math.ceil(fontSize / baseGridUnit)
    return numberOfUnits * baseGridUnit
  }

  
  // Function to create a string for negative indices
  function formatNegativeIndex(index: number) {
    const absoluteValue = Math.abs(index)
    if (absoluteValue % 1 === 0)
      return `-${absoluteValue}`

    return `-${(absoluteValue * 10).toFixed(0).padStart(2, '0')}`
  }

  // Function to create a string for positive, non-integer indices
  function formatPositiveNonIntegerIndex(index: number) {
    return `${(index * 10).toFixed(0).padStart(2, '0')}`
  }

  // A separate function to generate a CSS-valid string for variable naming based on the loop index
  function generateIndexString(index: number) {
    // Check if the index is negative
    if (index < 0)
      return formatNegativeIndex(index)

    // Check if the index is positive and non-integer
    if (index > 0 && index % 1 !== 0)
      return formatPositiveNonIntegerIndex(index)

    // If index is 0 or a positive integer, just return its string representation
    return index.toString()
  }






  const updateActiveScale = (newWindowWidth: number) => {
    // Check if scales is defined and has numeric keys (breakpoints)
    if ($s.scales && Object.keys($s.scales).every(key => !isNaN(Number(key)))) {
      const sortedBreakpoints = Object.keys($s.scales).map(Number).sort((a, b) => a - b)

      let appliedBreakpoint: number | undefined
      // Check and apply breakpoint if found
      for (const breakpoint of sortedBreakpoints) {
        if (newWindowWidth <= breakpoint) {
          Object.assign($s.scale, $s.scales[breakpoint])
          appliedBreakpoint = breakpoint
          break
        }
      }

      // If window width exceeds largest breakpoint or no applicable breakpoint found, use default scale.
      if (!appliedBreakpoint || newWindowWidth > Math.max(...sortedBreakpoints))
        Object.assign($s.scale, defaultScale)

      // Only run `init()` if the applied breakpoint has changed to avoid unnecessary updates
      if (appliedBreakpoint !== lastAppliedBreakpoint) {
        lastAppliedBreakpoint = appliedBreakpoint
        initTypeScales()
      }
    }
    else {
      // If scales are not defined or invalid, ensure default settings are applied
      Object.assign($s.scale, defaultScale)
      // Ensure init is run in case it’s the first load or scales has become invalid
      if (lastAppliedBreakpoint !== undefined) {
        lastAppliedBreakpoint = undefined
        initTypeScales()
      }
    }
  }

  

  watch(() => $s.$state, () => {
    if (typeof document !== 'undefined')
      initTypeScales()
  }, { immediate: true })

  watch([fontSize, gridRatio, ratio, scales], () => {
    if (isMounted)
      initTypeScales()
  }, {
    deep: true,
  })

  // UPDATE the scale variables when the window width changes
  // Check if a media point is defined for the new width, if so, use those values, otherwise use the default values
  watch(ww, (width) => {
    if (width && typeof width === 'number') {
      updateActiveScale(width)
    }
  }, { immediate: true })

  return {
    initGlobalVariables,
    initTypeScales
  }
}
